<template>
  <span
    :class="`request-page-sidefilter ${
      sidebarHasMinWidth ? 'request-sidefilter-min-width' : ''
    }`"
  >
    <div class="aside-header">
      <h2 class="filters-title has-text-weight-semibold">Filters</h2>
      <NrButton
        class="reset-filters-btn"
        variant="minimal-neutral"
        aria-label="Reset Filters"
        @click="handleSavedView(null)"
      >
        Reset filters
      </NrButton>
    </div>
    <div
      :class="`aside-content has-background-white-bis ${
        device === 'isMobile' ? 'm-right-12' : ''
      } ${isSmallScreen() ? '' : 'specified-height-aside'}`"
    >
      <div
        class="savedviews-select-container aside-content-savedviews m-bottom-12"
        v-if="notGuest"
      >
        <NrSelect
          @option:deselecting="handleSavedView"
          @input="handleSavedView"
          :options="savedViews"
          :modelValue="selectedSavedView"
          label="filter_name"
          placeholder="Select or search"
        >
          <template v-slot:label>
            <NrTooltip
              label="Select filters then click the create filtered view button."
              position="right"
            >
              Saved filtered views
              <NrIcon class="inline-icon" name="info-circle" />
            </NrTooltip>
          </template>
        </NrSelect>

        <div class="saved-view-btns">
          <NrButton
            @click="isComponentModalActive = true"
            :disabled="allFiltersDeselected"
            aria-label="Apply filters to create a new view"
            variant="bordered-neutral"
          >
            Create filtered view
          </NrButton>

          <NrTooltip
            :label="
              !!selectedSavedView
                ? 'Delete current active saved view'
                : 'Please select a saved view to delete'
            "
            position="left"
          >
            <NrIconButton
              variant="bordered-danger"
              :icon="{ name: 'trash-alt' }"
              @click="isDeleteModalActive = true"
              :disabled="!selectedSavedView"
              class="saved-view-btns-delete"
              aria-label="Delete active saved view"
            />
          </NrTooltip>
        </div>
      </div>
      <NrModal
        class="saved-view-delete-modal"
        :show="isDeleteModalActive"
        title="Delete saved view"
        primary-button-text="Delete saved view"
        @on-primary-click="handleDeleteSavedView"
        @on-secondary-click="isDeleteModalActive = false"
        @on-close-click="isDeleteModalActive = false"
        has-secondary-button
      >
        <p v-if="selectedSavedView">
          You are about to delete the saved view “{{
            selectedSavedView.filter_name
          }}”. This action cannot be undone. Do you want to continue?
        </p>
      </NrModal>

      <NrModal
        :show="isComponentModalActive"
        title="Save View"
        primary-button-text="Save"
        @on-primary-click="handleEmitSaveView"
        @on-secondary-click="isComponentModalActive = false"
        @on-close-click="isComponentModalActive = false"
        has-secondary-button
        :disableButtons="!saveViewName.length"
      >
        <NrFormInput
          maxLength="30"
          label="Save view"
          placeholder="Name your saved view"
          required
          v-model="saveViewName"
        />
      </NrModal>
      <div class="filter-inputs">
        <NrFormInput
          label="Search requests"
          :modelValue="filters['search_term']"
          aria-label="Type here to filter by document name"
          placeholder="Search by keyword in a request"
          withTooltip
          iconTooltipText="ctrl + enter to apply filters"
          id="request-search-field"
          @input="setFilters({ search_term: $event })"
          @focus="$emit('onSearchRequestFieldFocus')"
          @focusout="$emit('onSearchRequestFieldFocusOut')"
        />

        <section class="filters checkbox-section" v-if="currentUser && myId">
          <NrFormLabel>My requests</NrFormLabel>
          <NrFormInput
            v-if="notGuest"
            type="checkbox"
            class="checkbox"
            label="Point of contact"
            :modelValue="isMyRequestChecked('request_poc_ids')"
            @input="formatMyRequestsFilter('request_poc_ids', $event)"
          />
          <NrFormInput
            v-if="notGuest"
            type="checkbox"
            class="checkbox"
            label="Staff"
            :modelValue="isMyRequestChecked('request_helper_ids')"
            @input="formatMyRequestsFilter('request_helper_ids', $event)"
          />
          <NrFormInput
            type="checkbox"
            label="Requester"
            class="checkbox"
            :modelValue="isMyRequestChecked('requester_ids')"
            @input="formatMyRequestsFilter('requester_ids', $event)"
          />
        </section>

        <section
          v-for="(section, index) in filtersJson.checkboxes"
          :key="index + section.name"
          class="filters checkbox-section"
        >
          <NrFormLabel v-if="canShowFilterLabelForGuest(section)">
            {{ section.name }}
          </NrFormLabel>
          <NrFormInput
            v-for="(item, index) in filteredFields(section.checkboxes)"
            :key="index"
            class="checkbox"
            type="checkbox"
            :modelValue="filters[item.field]"
            @input="setFilters({ [item.field]: $event })"
          >
            <div class="is-mobile level m-right-8">
              <div class="side-filter-icons" aria-hidden="true">
                <NrIcon :name="item.icon" class="m-right-8" />
              </div>
              <span class="level-item">{{ item.label }}</span>
            </div>
          </NrFormInput>
        </section>

        <NrDatePicker
          v-if="
            canShowFilterField({
              field: 'request_date_start_date',
              showGuest: false
            })
          "
          :modelValue="filters['request_date_start_date']"
          :maxDate="filters['request_date_end_date']"
          @input="setFilters({ request_date_start_date: $event })"
        >
          <template v-slot:label>Request date (start)</template>

          <template v-slot:trigger>
            <NrIcon name="calendar-alt" />
          </template>
        </NrDatePicker>

        <NrDatePicker
          v-if="
            canShowFilterField({
              field: 'request_date_end_date',
              showGuest: false
            })
          "
          :modelValue="filters['request_date_end_date']"
          :minDate="filters['request_date_start_date']"
          @input="setFilters({ request_date_end_date: $event })"
        >
          <template v-slot:label>Request date (end)</template>

          <template v-slot:trigger>
            <NrIcon name="calendar-alt" />
          </template>
        </NrDatePicker>

        <NrDatePicker
          v-if="
            canShowFilterField({
              field: 'due_date_start_date',
              showGuest: false
            })
          "
          :modelValue="filters['due_date_start_date']"
          :maxDate="filters['due_date_end_date']"
          @input="setFilters({ due_date_start_date: $event })"
        >
          <template v-slot:label>Due date (start)</template>

          <template v-slot:trigger>
            <NrIcon name="calendar-alt" />
          </template>
        </NrDatePicker>

        <NrDatePicker
          v-if="
            canShowFilterField({
              field: 'due_date_end_date',
              showGuest: false
            })
          "
          :modelValue="filters['due_date_end_date']"
          :minDate="filters['due_date_start_date']"
          @input="setFilters({ due_date_end_date: $event })"
        >
          <template v-slot:label>Due date (end)</template>

          <template v-slot:trigger>
            <NrIcon name="calendar-alt" />
          </template>
        </NrDatePicker>

        <NrSelect
          v-if="$ability.can('read', 'staff_only_items')"
          name="poc"
          placeholder="Search by name or email"
          :options="
            parseOptionsForNrSelect(filterOptions.pocs, 'name_with_email')
          "
          :loading="filterFetchState.pocs.fetching"
          :hasMore="!filterFetchState.pocs.end"
          :modelValue="filterOptionValues('request_poc_ids')"
          :filterable="false"
          @search="handleFilterTyping($event, 'pocs')"
          @input="handleAutogeneratedDataSelect($event, 'request_poc_ids')"
          @load-more="handleFilterScroll('pocs')"
          @open="handleFilterTyping($event, 'pocs')"
          multiple
        >
          <template v-slot:label>
            <NrTooltip
              :label="`Only point of contact active on at least one request will appear`"
              position="right"
            >
              Point of contact
              <NrIcon class="inline-icon" name="info-circle" />
            </NrTooltip>
          </template>
        </NrSelect>

        <NrSelect
          v-for="(filter, index) in autoGenerateDataFilter"
          :key="index"
          :name="filter.data"
          :placeholder="filter.placeholder"
          :options="
            parseOptionsForNrSelect(filterOptions[filter.data], filter.field)
          "
          :loading="filterFetchState[filter.data].fetching"
          :hasMore="!filterFetchState[filter.data].end"
          :modelValue="filterOptionValues(filter.dataKeyName)"
          :filterable="false"
          @search="handleFilterTyping($event, filter.data)"
          @input="handleAutogeneratedDataSelect($event, filter.dataKeyName)"
          @load-more="handleFilterScroll(filter.data)"
          @open="handleFilterTyping($event, filter.data)"
          multiple
        >
          <template v-slot:label>
            <NrTooltip
              :label="`Only ${filter.label.toLowerCase()} active on at least one request will appear`"
              position="right"
            >
              {{ filter.label }}
              <NrIcon class="inline-icon" name="info-circle" />
            </NrTooltip>
          </template>
        </NrSelect>
        <div class="filters-submit aside-footer">
          <NrButton
            class="m-right-12 is-hidden-desktop"
            variant="bordered-primary"
            aria-label="Cancel"
            @click="$emit('toggle-filter')"
          >
            Cancel
          </NrButton>
          <NrTooltip label="ctrl+enter to apply filters">
            <NrButton @click="$emit('apply-filters')"> Apply filters </NrButton>
          </NrTooltip>
        </div>
      </div>
    </div>
  </span>
</template>

<script lang="ts">
import { debounce, isEqual } from 'lodash'
import { mapActions, mapState, mapMutations } from 'vuex'
import filtersJson from './filters.json'
import { isMobile, isTablet } from 'mobile-device-detect'
import togglePageScroll from '../../../mixins/toggle-page-scroll'
import NrDatePicker from '@nextrequestco/components/src/components/NrDatePicker'
import NrIcon from '@nextrequestco/components/src/components/NrIcon'
import NrModal from '@nextrequestco/components/src/components/NrModal'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip'
import NrButton from '@nextrequestco/components/src/components/NrButton'
import NrIconButton from '@nextrequestco/components/src/components/NrIconButton'
import NrSelect from '@nextrequestco/components/src/components/NrSelect'
import NrFormInput from '@nextrequestco/components/src/components/NrFormInput'
import NrFormLabel from '@nextrequestco/components/src/components/form-elements'
import {
  QUERY_FILTER_IDS_TO_OPTION_KEY_MAP,
  filterState
} from '../../../store/filters-state'

export default {
  components: {
    NrDatePicker,
    NrIcon,
    NrModal,
    NrTooltip,
    NrButton,
    NrSelect,
    NrFormInput,
    NrIconButton,
    NrFormLabel
  },
  mixins: [togglePageScroll],
  props: {
    notGuest: { type: Boolean, default: true },
    label: { type: String, default: 'Save Views' },
    isDisabled: { type: Boolean, default: false },
    currentUser: { type: Object },
    previewUserId: { type: [String, Number] },
    account: { type: Object, default: () => ({}) },
    filtersHeight: { type: String, default: '300' },
    heroOpen: { type: Boolean },
    sidebarHasMinWidth: { type: Boolean }
  },
  data() {
    return {
      isDeleteModalActive: false,
      height: 300,
      device: null,
      isComponentModalActive: false,
      saveViewName: '',
      filtersJson,
      autoGenerateData: filtersJson.autoGenerateData
    }
  },
  mounted() {
    this.height = this.filtersHeight

    if (isMobile) {
      this.device = 'isMobile'
    } else if (isTablet) {
      this.device = 'isTable'
    }
    this.togglePageScroll()
  },
  watch: {
    filtersHeight() {
      this.height = this.filtersHeight
    }
  },
  computed: {
    ...mapState('requests', [
      'savedViews',
      'selectedSavedView',
      'filters',
      'filterOptions',
      'filterFetchState',
      'requests',
      'height'
    ]),

    allFiltersDeselected() {
      return isEqual(this.filters, filterState)
    },

    autoGenerateDataFilter() {
      return Object.keys(this.filtersJson.autoGenerateData)
        .map((filterKey) => {
          return this.filtersJson.autoGenerateData[filterKey]
        })
        .filter((filter) => this.canShowFilterField(filter))
    },

    myId() {
      return this.previewUserId || this.currentUser.id
    },

    asideMaxHeight() {
      return this.height + 'px'
    }
  },
  methods: {
    ...mapActions('requests', [
      'fetchFilterOptions',
      'setAddSavedViews',
      'setDeleteSelectedView',
      'fetchRequests',
      'applyFilters',
      'configureRequestsTableColumns',
      'fetchOptionsFromFilterState'
    ]),

    ...mapMutations('requests', [
      'setSelectedSavedView',
      'setFilterOptions',
      'resetFilters'
    ]),

    filteredFields(checkboxes) {
      return checkboxes.filter((item) => this.canShowFilterField(item))
    },

    filterOptionValues(filterName) {
      const arry = this.filters[filterName]
        .map((id) => {
          return this.filterOptions[
            QUERY_FILTER_IDS_TO_OPTION_KEY_MAP[filterName]
          ].find((option) => option.id === id)
        })
        .filter((option) => !!option)
      return this.parseOptionsForNrSelect(arry, 'name_with_email')
    },

    async setFilters(filters) {
      this.setSelectedSavedView(null)
      this.$cookies.remove('savedView')
      await this.$store.dispatch('requests/setFilters', filters)
    },

    isMyRequestChecked(filterName) {
      if (!this.currentUser) return false
      return this.filters[filterName].includes(this.myId)
    },

    formatMyRequestsFilter(filter, enable) {
      // NOTE: Non-signed in users cannot see this UI
      if (!this.currentUser) return

      if (filter !== 'requester_ids') {
        this.setFilterOptions({
          [QUERY_FILTER_IDS_TO_OPTION_KEY_MAP[filter]]: [
            ...this.filterOptions[QUERY_FILTER_IDS_TO_OPTION_KEY_MAP[filter]],
            this.currentUser
          ]
        })
      }

      if (enable) {
        this.setFilters({
          [filter]: [...this.filters[filter], this.myId]
        })
      } else {
        this.setFilters({
          [filter]: this.filters[filter].filter(
            (id) => Number(id) !== Number(this.myId)
          )
        })
      }
    },

    async handleEmitSaveView() {
      this.isComponentModalActive = false
      try {
        await this.setAddSavedViews(this.saveViewName)
        this.saveViewName = ''
        this.$cookies.set('savedView', this.savedViews[0].id)
        this.$toasted.show('Saved view has been added!', {
          type: 'success',
          duration: 1000
        })
        this.fetchRequests().finally(() => {
          if (this.requests.length) {
            this.configureRequestsTableColumns(this.$ability)
          }
          this.togglePageScroll()
        })
      } catch (error) {
        this.$toasted.error('Saved view was not able to be saved!', {
          duration: 2000
        })
      }
    },

    async handleSavedView(option) {
      this.resetFilters()

      if (option) {
        await this.setFilters(option.filters)
        this.$cookies.set('savedView', option.id)
      } else {
        this.$cookies.remove('savedView')
      }

      // Call apply filters.
      this.applyFilters()
      this.setSelectedSavedView(option)
      this.fetchOptionsFromFilterState()
    },

    handleDeleteSavedView() {
      const savedViewId = this.selectedSavedView.id
      if (!savedViewId) return
      this.setDeleteSelectedView(savedViewId)
      this.isDeleteModalActive = false
    },

    handleFilterTyping: debounce(async function (name = '', filter) {
      if (!filter) return
      this.fetchFilterOptions({ filterName: filter, search_text: name })
    }, 500),

    handleFilterScroll: debounce(function (filter) {
      this.handleFilterTyping(this.filterFetchState[filter].search_text, filter)
    }, 250),

    canShowFilterLabelForGuest(filter) {
      // Don't show the 'Request visibility' label if you are a guest or not signed in
      return !(
        filter.name === 'Request visibility' &&
        !this.$ability.can('read', 'staff_only_items')
      )
    },

    canShowFilterField(filter) {
      // Show the filter field if you are staff OR if 'filter.showGuest' is
      // true and if you are a guest or not signed in
      const accountSetting = {
        embargoed: this.account.embargoing_available,
        department_published: this.account.restricted_visibilities_feature_flag
      }
      if (['embargoed', 'department_published'].includes(filter.field)) {
        return (
          (this.$ability.can('read', 'staff_only_items') ||
            (filter.showGuest &&
              !this.$ability.can('read', 'staff_only_items'))) &&
          this.account &&
          accountSetting[filter.field]
        )
      }
      return (
        this.$ability.can('read', 'staff_only_items') ||
        (filter.showGuest && !this.$ability.can('read', 'staff_only_items'))
      )
    },

    parseOptionsForNrSelect(options, keyName): [] {
      if (!Array.isArray(options)) {
        options = [options]
      }

      return options
        .filter(
          (option) => option && (option[keyName] || option.label || option.name)
        )
        .map((option) => {
          return {
            value: option.id,
            label: option[keyName] || option.label || option.name
          }
        })
    },

    handleAutogeneratedDataSelect(selected, filter) {
      this.setFilters({ [filter]: selected.map((item) => item.value) })
    }
  }
}
</script>

<style lang="scss">
@import '@nextrequestco/components/src/styles/tokens/tokens.scss';

.request-page-sidefilter {
  max-width: 500px;
  width: 100%;
  display: flex;
  flex-flow: column;
  overflow: hidden;

  .inline-icon {
    display: inline-block;
    vertical-align: middle;
  }

  .aside {
    &-header {
      min-height: 40px;
      .filters-title {
        font-size: $font-size-4;
        display: inline-block;
        padding: $spacing-4 $spacing-4 $spacing-4 $spacing-12;
      }
    }
    &-content {
      overflow-y: auto;
      overflow-x: hidden;
      padding: $spacing-12;
      &-savedviews {
        .saved-view-btns {
          display: flex;
          justify-content: flex-end;
          &-delete {
            margin-left: $spacing-8;
          }
        }
      }
      .aside-footer {
        background: rgba(249, 249, 250, 0.7);
        height: 86px;
        padding: $spacing-24 0px $spacing-24 0;
        display: flex;
        justify-content: flex-end;
        position: sticky;
        bottom: 0;
        z-index: 2;
      }
    }
  }
  .checkbox-section {
    .checkbox {
      display: flex;
      justify-content: flex-end;
      margin-bottom: $spacing-4;
    }
    .nr-field {
      .side-filter-icons {
        font-size: $font-size-4;
      }
    }
  }
}

.specified-height-aside {
  height: 100%;
  max-height: v-bind(asideMaxHeight);
}

.request-sidefilter-min-width {
  min-width: 100vw;
}
</style>
